/* Batch 10 · Stage A (A1, A5) — Copy engagement dialog.
   New name (default "<name> (copy)"), the same financial year, four ticks (default ON). The copy runs as a background job;
   this dialog shows its progress line until it is done, then offers to open the copy. Partner / Manager only (server-checked). */
import React, { useEffect, useRef, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { capJobText } from "@/lib/engagementCap";   // Pre-Step 4 · 4b: a refused job shows the cap text (EN / AR)

const TICKS = [
  ["include_gl_tb", "engagements.copyTickGl"],
  ["include_documents", "engagements.copyTickDocs"],
  ["include_samples", "engagements.copyTickSamples"],
  ["include_misstatements", "engagements.copyTickMisst"],
];

export function defaultCopyName(eng) {
  // Batch 14 · M13 (F-112): the copy's ENGAGEMENT NAME (the client's legal name is copied unchanged for the report / FS)
  const base = (eng?.engagement_name || eng?.firm_engagement_ref || eng?.client_name || "").trim();
  return `${base} (copy)`.trim();
}

export default function CopyEngagementDialog({ eng, open, onOpenChange, onCopied }) {
  const { t, lang } = useI18n();
  const [name, setName] = useState("");
  const [ref, setRef] = useState("");   // Batch 14 · M13: the copy's firm engagement reference, shown separately
  const [ticks, setTicks] = useState({ include_gl_tb: true, include_documents: true, include_samples: true, include_misstatements: true });
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (open && eng) {
      setName(defaultCopyName(eng));
      setRef(eng?.firm_engagement_ref || "");
      setTicks({ include_gl_tb: true, include_documents: true, include_samples: true, include_misstatements: true });
      setJob(null); setBusy(false);
    }
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [open, eng]);

  const poll = (id) => {
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/jobs/${id}`, { _silent: true });
        setJob(r.data);
        if (r.data.status === "done") {
          setBusy(false);
          toast.success(t("engagements.copyDone").replace("{name}", r.data.result?.name || name));
          onCopied && onCopied(r.data.result || {});
          return;
        }
        if (r.data.status === "failed") {
          setBusy(false);
          toast.error(capJobText(r.data, lang) || t("engagements.copyFailed"));   // Pre-Step 4 · 4b: the cap refusal EN / AR
          return;
        }
      } catch (_) { /* keep polling */ }
      poll(id);
    }, 1200);
  };

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/engagements/${eng.id}/copy`, { name, firm_engagement_ref: ref, ...ticks });
      setJob({ id: r.data.job_id, status: "queued", progress_pct: 0, message: t("engagements.copyQueued") });
      poll(r.data.job_id);
    } catch (e) {
      setBusy(false);
      toast.error(toErrMsg(e, t("engagements.copyFailed")));
    }
  };

  const pct = Math.max(0, Math.min(100, Number(job?.progress_pct || 0)));
  const done = job?.status === "done";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="eng-copy-dialog">
        <DialogHeader>
          <DialogTitle>{t("engagements.copyTitle")}</DialogTitle>
          <DialogDescription>{t("engagements.copyDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label htmlFor="eng-copy-name">{t("engagements.copyName")}</Label>
            <Input id="eng-copy-name" value={name} onChange={(e) => setName(e.target.value)} disabled={busy || done} data-testid="eng-copy-name" />
            <div className="mt-0.5 text-[11px] text-muted-foreground">{t("engagements.copyNameHint").replace("{client}", eng?.client_name || "")}</div>
          </div>
          <div>
            <Label htmlFor="eng-copy-ref">{t("engagements.copyRef")}</Label>
            <Input id="eng-copy-ref" value={ref} onChange={(e) => setRef(e.target.value)} disabled={busy || done} data-testid="eng-copy-ref" />
          </div>
          <div className="text-xs text-muted-foreground" data-testid="eng-copy-fy">{t("engagements.copySameFy").replace("{fy}", String(eng?.financial_year || ""))}</div>
          <div className="space-y-2">
            {TICKS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2">
                <Checkbox checked={!!ticks[k]} disabled={busy || done} onCheckedChange={(v) => setTicks({ ...ticks, [k]: !!v })} data-testid={`eng-copy-${k}`} />
                <span>{t(label)}</span>
              </label>
            ))}
          </div>
          <div className="rounded border bg-slate-50 p-2 text-xs text-muted-foreground dark:bg-slate-900">{t("engagements.copyNever")}</div>
          {job && (
            <div data-testid="eng-copy-progress">
              <div className="h-2 w-full rounded bg-slate-200"><div className={`h-2 rounded ${job.status === "failed" ? "bg-red-600" : "bg-emerald-600"}`} style={{ width: `${done ? 100 : pct}%` }} /></div>
              <div className="mt-1 text-xs text-muted-foreground" data-testid="eng-copy-line">{done ? t("engagements.copyDone").replace("{name}", job.result?.name || name) : (job.status === "failed" ? t("engagements.copyFailed") : (job.message || t("engagements.copyQueued")))} {!done && job.status !== "failed" ? `· ${pct}%` : ""}</div>
            </div>
          )}
        </div>
        <DialogFooter>
          {!done && <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>}
          {!done && <Button disabled={busy || !name.trim()} onClick={start} data-testid="eng-copy-start">{busy ? t("engagements.copyRunning") : t("engagements.copyStart")}</Button>}
          {done && <Button onClick={() => onOpenChange(false, job.result)} data-testid="eng-copy-open">{t("engagements.copyOpen")}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
